import React from 'react';

import { QueryClient, QueryClientProvider } from 'react-query';

const retryDelays = [1000, 2000, 4000, 8000, 16000];

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60,
            // Only retry on 429 (rate limited) or 5xx (server errors) — retrying
            // 4xx client errors (401, 403, 404) wastes requests and slows the UI
            // since those errors will never resolve on their own.
            retry: (failureCount, error: any) => {
                const status = error?.response?.status;
                if (status === 429 || (status >= 500 && status < 600)) {
                    return failureCount < 5;
                }
                return false;
            },
            // Add up to 1s of random jitter to each delay so that multiple
            // queries failing at the same time don't all retry simultaneously
            // (thundering herd), which would amplify the rate limit problem.
            retryDelay: (attemptIndex) => {
                const base = retryDelays[attemptIndex] ?? 16000;
                return base + Math.random() * 1000;
            },
            refetchOnWindowFocus: true,
        },
    }
});

interface Props {
    children: React.ReactNode
}

const ApiServiceProvider: React.FC<Props> = ({ children }) => {
    return (
        <QueryClientProvider client={queryClient}>
            {children}
        </QueryClientProvider>
    );
};

export default ApiServiceProvider;